import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { theme } from '@garnish/constants';
import { BodyText, Icon, IconLink } from '@sg/garnish';

import { type PaymentMethod } from '../../machine/scan-to-pay.machine.types';
import {
  iconBackgroundPalettes,
  iconPalettes,
  lightIconPalettes,
  PAYMENT_METHOD_ICON_MAP,
  type ScanToPayPalette,
  textPalettes,
  warningIconPalettes,
  warningTextPalettes,
} from '../../utils';

export const ScanToPayPaymentMethod = (props: ScanToPayPaymentMethodProps) => {
  const { paymentMethodId, paymentMethods, palette, onPaymentMethodSelection } =
    props;

  const paymentMethod = paymentMethods.find(
    (paymentMethod) => paymentMethod.id === paymentMethodId,
  );

  const { formatMessage } = useIntl();
  const a11yLabel = formatMessage(messages.a11yLabel);
  const expirationMessage = useExpirationMessage(paymentMethod);

  if (!paymentMethod) {
    return null;
  }

  return (
    <View style={styles.container}>
      <View style={styles.leftSide}>
        <Icon
          color={iconPalettes[palette]}
          name={PAYMENT_METHOD_ICON_MAP[paymentMethod.cardType]}
          width={32}
          height={32}
        />

        <View>
          <BodyText sizeMatch={['14']} style={textPalettes[palette]}>
            <FormattedMessage {...messages.title} />
          </BodyText>

          <BodyText bold sizeMatch={['16']} style={textPalettes[palette]}>
            {paymentMethod.nickname ?? paymentMethod.description}
          </BodyText>

          {expirationMessage ? (
            <View style={styles.warning}>
              <Icon
                color={warningIconPalettes[palette]}
                name="IconInfo"
                width={16}
                height={16}
              />

              <BodyText sizeMatch={['14']} style={warningTextPalettes[palette]}>
                {expirationMessage}
              </BodyText>
            </View>
          ) : null}
        </View>
      </View>

      <IconLink
        name="IconList"
        width={24}
        height={24}
        accessibilityLabel={a11yLabel}
        color={lightIconPalettes[palette]}
        style={[styles.cta, iconBackgroundPalettes[palette]]}
        onPress={onPaymentMethodSelection}
      />
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const useExpirationMessage = (paymentMethod?: PaymentMethod) => {
  const { formatMessage } = useIntl();

  if (paymentMethod?.expirationState === 'EXPIRED') {
    return formatMessage(messages.expired);
  }

  if (paymentMethod?.expirationState === 'NEARING') {
    return formatMessage(messages.expiring);
  }

  return null;
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Payment method',
    description: 'Scan To Pay | Payment method | Title',
  },
  a11yLabel: {
    defaultMessage: 'Change payment method',
    description: 'Scan To Pay | Payment method | Accessibility Label',
  },
  expiring: {
    defaultMessage: 'Card will expire soon',
    description: 'Scan To Pay | Payment method | Expiring',
  },
  expired: {
    defaultMessage: 'Card expired',
    description: 'Scan To Pay | Payment method | Expired',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing['4'],
  },
  leftSide: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['4'],
  },
  warning: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['1'],
  },
  cta: {
    width: 40,
    height: 40,
    borderRadius: 40,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ScanToPayPaymentMethodProps = {
  paymentMethodId: string;
  paymentMethods: readonly PaymentMethod[];
  palette: ScanToPayPalette;
  onPaymentMethodSelection: () => void;
};
